import { watch, ref, shallowRef, ShallowRef, computed } from 'vue';
import { MeetingEvent, ZoomMeeting } from '../meeting/zoom'
import useMeetingConfiguration from './useMeetingConfiguration';
import DeviceUtility from '../helpers/device';


const _lastMicrophone = ref('' as string | undefined)
const _lastCamera = ref('' as string | undefined)
const _lastSpeaker = ref('' as string | undefined)
const _lastMicrophoneMuted = ref(false)
const _lastCameraMuted = ref(false)
const _meeting = shallowRef<ZoomMeeting>()
const _failed = ref(false)
const _failedMessage = ref('')

const createMeeting = () => {
	const meetingConfiguration = useMeetingConfiguration()
	_meeting.value = new ZoomMeeting(meetingConfiguration.getPingUrl())
	watch(() => _meeting.value?.activeCamera, (newDevice) => {
		// save the last used camera
		const meetingConfiguration = useMeetingConfiguration()
		const settings = meetingConfiguration.getSettings()
		if (newDevice) {
			settings.activeCameraId = newDevice
		}
	})
}
const getMeeting = () : ShallowRef<ZoomMeeting> => {
	if (!_meeting.value) {
		createMeeting()
	}
	if(_meeting == null){
		throw new Error('Meeting is not initialized. Call join() to initialize it.');
	}
	return _meeting as ShallowRef<ZoomMeeting>
}

// load the asset provider from the settings

let _reloadTimeout = 0
let _reloading = false
let _joinCount = 0
let _restarting = false

const handleResponse = async function (response: Response) {
	if (response.ok) {
		return await response.json();
	} else {
		let data = await response.json();
		data.code = response.status;
		return Promise.reject(data);
	}
};

const getVersion = async () => {
	const meetingConfiguration = useMeetingConfiguration()
	let response = await fetch(meetingConfiguration.getPingUrl());
	return handleResponse(response);
}

const checkAndReload = () => {
    if(_reloading){
        console.warn('Already reloading, ignoring')
        return
    }
	_reloading = true

    clearTimeout(_reloadTimeout)
	getVersion().then((r) => {
		if(r.version){
			// success
            console.warn('Restart requested...', r.version)
			restart()
		}else{
            // error
            console.error('Error getting version', r)
            _reloadTimeout = window.setTimeout(() => {
                checkAndReload()
            }, 2000)
        }
	}).catch((error) => {
		console.error('Error checking sdk version', error)
		_reloadTimeout = window.setTimeout(() => {
			checkAndReload()
		}, 2000)
	}).finally(() => {
        _reloading = false
    })
}

const restart = async () => {
	try{
        if(_restarting){
            // if the meeting is initializing, we want to destroy it and start over
            console.warn('Meeting restarting, ignoring new restart request')
            return
        }
        _restarting = true

		// save state if the meeting was initialized properly
		if(getMeeting().value.isInitialized){
			_lastCamera.value = getMeeting().value.activeCamera
			_lastMicrophone.value = getMeeting().value.activeMicrophone
			_lastSpeaker.value = getMeeting().value.activeAudioSpeaker
			_lastCameraMuted.value = getMeeting().value.isCameraMuted ?? false
			_lastMicrophoneMuted.value = getMeeting().value.isMicrophoneMuted ?? false
		}

		await getMeeting().value.destroy(true)
		
	}catch(e){
		console.warn('Error while destroying....ignoring', e)
	}
	createMeeting()
	join()
};

const handleFailover = () =>{
	console.log('handleFailover mic/camera muted state: ', getMeeting().value.isMicrophoneMuted, getMeeting().value.isCameraMuted)
	checkAndReload()
};

// if we have 3 join attempts in 12s, something is wrong
const _joinTimeout = 12000
const isFailingFast = () => {
	if(_joinCountHistory.length < 3){
		return false
	}
	const now = new Date().getTime()
	const lastThreeJoins = _joinCountHistory.slice(-3)
	const firstJoinTime = lastThreeJoins[0]
	const lastJoinTime = lastThreeJoins[lastThreeJoins.length - 1]
	const timeDiff = lastJoinTime - firstJoinTime
	if(timeDiff < _joinTimeout){
		console.warn(`Failing fast: ${_joinCountHistory.length} joins in ${timeDiff}ms`)
		return true
	}
	console.warn(`Not failing fast: ${_joinCountHistory.length} joins in ${timeDiff}ms`)
	return false
}

const _joinCountHistory: number[] = []
const join = () => {
    _joinCount++
	_joinCountHistory.push(new Date().getTime())
	if(isFailingFast()){
		console.warn('Failing fast, not joining!')
		getMeeting().value.destroy(false, true)
		return
	}
	const meetingConfiguration = useMeetingConfiguration()
	const settings = meetingConfiguration.getSettings()

	getMeeting().value.isVirtualBackgroundActive = settings.isVirtualBackgroundActive
	getMeeting().value.virtualBackgroundUrl = settings.virtualBackgroundImageUrl
	getMeeting().value.isCameraRequired = settings.isCameraRequired
	getMeeting().value.disableTorch = settings.disableTorch
	

	// default camera/mic setting
	getMeeting().value.defaultCameraOff = meetingConfiguration.getSettings().defaultCameraOff
	getMeeting().value.defaultMicrophoneOff = meetingConfiguration.getSettings().defaultMicrophoneOff

	if(meetingConfiguration.getSettings().spotlightVisitor){
		getMeeting().value.autoSpotlightFunction = () => {
			return getMeeting().value.getFirstActiveNonHostOrManagerParticipantId()
		}
	}

	// auto-start recordings when we get a participant for the first time
	const isAutoStartRecordingDone = () => {
		return localStorage.getItem('autoStartRecordingDone-' + getMeeting().value.sessionId) == 'true';
	}
	const setAutoStartRecordingDone = () => {
		localStorage.setItem('autoStartRecordingDone-' + getMeeting().value.sessionId, 'true')
	}

	const removeAutoStartRecordingDone = () => {
		localStorage.removeItem('autoStartRecordingDone-' + getMeeting().value.sessionId)

		// remove all auto start recording done flags, since a given meeting should only happen 1x at a time
		Object.keys(localStorage).forEach(key => {
			if(key.startsWith('autoStartRecordingDone-')){
				localStorage.removeItem(key)
			}
		})
	}

	watch(() => getMeeting().value.isRecording, () => {
		if(getMeeting().value.isRecording){
			// set the flag to true if recording is ever started
			// this is used to prevent auto-starting the recording again
			// note that this actually happens if it's manually started also
			console.log('flagging auto start recording done')
			setAutoStartRecordingDone()
		}
	})
	
	watch(() => getMeeting().value.participants.length, (value) => {

		if(!getMeeting().value.isHost){
			// if we are not the host, we don't need to auto-start the recording
			// this is because only the host can start the recording automatically
			return;
		}

		const id = getMeeting().value.getFirstActiveNonHostOrManagerParticipantId()

		console.log('watching', id, isAutoStartRecordingDone(), getMeeting().value.isRecording, getMeeting().value.participants.length)

		if (
			getMeeting().value.sessionId &&
			!isAutoStartRecordingDone() &&  // do this only once
			value > 1 && // at least 2 people...
			getMeeting().value.participants.find(p => !p.isHost && !p.isManager) && // one of which is a visitor
			settings.autoStartRecording &&  // auto-start recording is enabled
			!getMeeting().value.isRecording // not already recording
			) { 

			// start recording and set the flag
			setAutoStartRecordingDone()
			getMeeting().value.isRecording = true
		}

		if(value == 1){
			// if there is only one participant, we can reset the auto start recording flag
			// allows auto-starting the recording again when the visitor rejoins
			removeAutoStartRecordingDone()
		}
		
	})

	const setBackCameraDefault = () =>{
		const shouldDefaultToBackCameraOnMobile = settings.shouldDefaultToBackCameraOnMobile
		// default the camera to the back if appropriate
		if (shouldDefaultToBackCameraOnMobile
			&& getMeeting().value.isMobileDevice) {
				// ensure we ignore any other camera settings - this one always wins
				getMeeting().value.preferredCameraId = 'environment'
		}
		console.log('setting back camera results:', shouldDefaultToBackCameraOnMobile, getMeeting().value.cameras.length, getMeeting().value.isMobileDevice)
	}
	
	getMeeting().value.on(MeetingEvent.FailoverChanged, (e: { failover: boolean}) => {
		if(e.failover == true){
			if(getMeeting().value.isMobileDevice && DeviceUtility.isAndroid){
				// this is a workaround for a bug in the zoom sdk where the audio fails on a failover
				// sdk version 2.2 at least has this bug
				console.warn('failover on android, maybe restarting')
				//handleFailover()
			}else{
				console.warn('failover on non-android, not restarting')
			}
		}else{
			console.warn('failover off, not restarting')
		}
	})

	getMeeting().value.on(MeetingEvent.ConnectionFailed, (e: any) => {
		console.warn('connection failed', e)
        handleFailover()
	})

	getMeeting().value.on(MeetingEvent.Initialized, (args: { meeting: ZoomMeeting }) => {
        console.log('meeting initialized, enabling restart - ' + getMeeting().value.id + ' - ' + args.meeting.id)
        _restarting = false
    })

	// on first join
	if(_joinCount == 1){
		// use the active camera id from the settings as the preferred camera id
		if(settings.activeCameraId){
			getMeeting().value.preferredCameraId = settings.activeCameraId
		}
		// set the default camera to the back camera if appropriate
		// we do this only if the join count is 1, because a rejoin will pick up the last used camera and we don't want to override that
		// but we DO want to override the active camera to the back camera ALWAYS if the setting is set
		setBackCameraDefault()
	}else{
		// on rejoin, we want to set the camera and microphone to the last used values
		// this is set in the handleFailover function
		if(_lastCamera.value){
			getMeeting().value.preferredCameraId = _lastCamera.value
		}
		if(_lastMicrophone.value){
			getMeeting().value.preferredMicrophoneId = _lastMicrophone.value
		}
		if(_lastSpeaker.value){
			getMeeting().value.preferredSpeakerId = _lastSpeaker.value
		}
	}

	

	// muted state is set after init
	getMeeting().value.init(settings.token)
		.then(() => {
			// and finally mute if appropriate
			if(_joinCount == 1){
				
			}else{
				console.log('setting camera/mic muted to last used value', _lastCameraMuted.value, _lastMicrophoneMuted.value)
				getMeeting().value.isCameraMuted = _lastCameraMuted.value
				getMeeting().value.isMicrophoneMuted = _lastMicrophoneMuted.value
			}
			console.log('meeting init callback')
		}).catch((e:any) => {
			_failed.value = true
			_failedMessage.value = e.message
		})
};

(window as any).handleFailover = handleFailover;
(window as any).checkAndReload = checkAndReload;
(window as any).restart = restart;
(window as any).getMeeting = getMeeting;
(window as any).join = join;

export const useMeetings = () => {
    return {
        getMeeting,
		clearMeeting: () => {
			_meeting.value = undefined
		},
		initFailed: computed(() => _failed.value),
		initFailedMessage: computed(() => _failedMessage.value),
        join
    }
}

(window as any).getMeetingStore = useMeetings;